import { api } from "@/lib/api/api";
import { MS_1_MINUTE, useAuthenticated } from "@gettactic/hooks";
import {
  isWorkingLocationsWithOOOFeaturesEnabled,
  orgRequiresReservationAfterPickingAnOffice
} from "@/lib/featureFlags";
import { isWorkingLocationOffice } from "@/lib/utils";
import {
  ISchedule,
  ISlot,
  IWorkingLocationPresenceType,
  Office,
  ResourceType,
  WL_PRESENCE_OFFICE,
  WL_PRESENCE_OOO,
  WL_PRESENCE_REMOTE
} from "@gettactic/api";
import { getTimeDateObjectFromString, parseApiDate } from "@gettactic/helpers";
import { useAvailableDays, defaultWeekDays } from "@gettactic/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { addDays, format, parse, startOfWeek } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useMyAgenda } from "@gettactic/hooks";
import { AgendaDay } from "@gettactic/api";
import { getFirstMondayBefore } from "@gettactic/helpers";
import { useUserOfficesAvailability } from './userOfficesAvailability';
import { uniq } from 'lodash';

interface OptimisticUpdate {
  location: IWorkingLocationPresenceType;
  officeId?: string;
}

export function useDashboardLocation({
  onDateChange
}: { onDateChange?: (date: string) => void } = {}) {
  const [optimisticUpdates, setOptimisticUpdates] = useState<
    Record<string, OptimisticUpdate>
  >({});
  const [loadingUpdates, setLoadingUpdates] = useState<Record<string, boolean>>(
    {}
  );

  const [oooRequest, setOooRequest] = useState<{
    date: string;
    type: "ooo" | "remote";
  } | null>(null);
  const [onConfirmDialogCallback, setOnConfirmDialogCallback] = useState<
    (() => void) | null
  >(null);
  const {
    userContext: { currentOffice, authenticatedUser, deviceTz }
  } = useAuthenticated();
  const [reserveModalData, setReserveModalData] = useState<{
    start: Date;
    resourceType: ResourceType;
    office: Office | undefined;
  } | null>(null);
  const [weekDate, setWeekDate] = useState<string>(() =>
    format(new Date(), "yyyy-MM-dd")
  );

  useEffect(() => {
    // for example, to keep updated date in a dashboard-wide filter
    onDateChange?.(weekDate);
  }, [weekDate]);

  const parsedDate = parse(weekDate, "yyyy-MM-dd", new Date());
  const firstMondayBefore = startOfWeek(parsedDate, { weekStartsOn: 1 });
  const firstMondayBeforeStr = format(firstMondayBefore, "yyyy-MM-dd");
  const endDateForWeekStr = format(addDays(firstMondayBefore, 7), "yyyy-MM-dd");

  const { data: availableDays } = useAvailableDays(currentOffice, {
    enabled: !!currentOffice.id,
    keepPreviousData: true,
    staleTime: MS_1_MINUTE
  });

  const queryClient = useQueryClient();

  const scheduleMeData = useMyAgenda({
    tz: currentOffice?.time_zone ?? deviceTz,
    user_tz: deviceTz,
    from: getFirstMondayBefore(weekDate)
  });

  // Compute office IDs and dates for availability check
  const { officeIds, dates } = useMemo(() => {
    if (!scheduleMeData.data) {
      return { officeIds: [], dates: [] };
    }

    const officeIds: string[] = [];
    const dates: string[] = [];
    
    for (const date in scheduleMeData.data.agenda) {
      const day = scheduleMeData.data.agenda[date];
      // Only fetch office availability for days where the user is in the office_id and has no reservations
      if (day.office_id && !day.schedules.length) {
        officeIds.push(day.office_id);
        dates.push(date);
      }
    }
    
    return { officeIds: uniq(officeIds), dates: uniq(dates) };
  }, [scheduleMeData.data]);

  const officeAvailabilityQuery = useUserOfficesAvailability({
    officeIds,
    dates
  });  
  const officeAvailabilityData = officeAvailabilityQuery?.data ?? null;
  const isLoadingOfficeAvailability = officeAvailabilityQuery?.isLoading ?? false;

  const nextWeekDate = scheduleMeData.data?.next_week_date;
  const previousWeekDate = scheduleMeData.data?.previous_week_date;

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries(["schedules"]);
    queryClient.invalidateQueries(["myAgenda"]);
    queryClient.invalidateQueries(["attendanceRequests"]);
    queryClient.invalidateQueries(["leavePolicies"]);
    // Also invalidate hours.rules to ensure availableDays stays fresh
    queryClient.invalidateQueries(["hours.rules"]);
  }, [queryClient]);

  const openReserveModal = (date: string, office: Office | undefined) => {
    setReserveModalData({
      start: parseApiDate(date),
      resourceType: "desk",
      office: office ?? undefined
    });
  };

  const updateWorkingLocation = async (
    newWorkingLocationId: IWorkingLocationPresenceType,
    targetDate: string, // YYYY-MM-DD
    dayNumber: number, // 0 - sunday, 6 - saturday
    agendaBeforeUpdate: AgendaDay | null
  ) => {
    const isOffice = isWorkingLocationOffice(newWorkingLocationId);
    const remotePolicy = agendaBeforeUpdate?.policy;

    const location = isOffice
      ? {
          office_id: newWorkingLocationId,
          location: WL_PRESENCE_OFFICE as IWorkingLocationPresenceType
        }
      : {
          location: newWorkingLocationId
        };

    const reservations =
      agendaBeforeUpdate?.schedules.filter((reservation: ISchedule) =>
        reservation.slots.some(
          (slot: ISlot) => slot.user?.id === authenticatedUser.user?.id
        )
      ) || [];

    const isChangingOffice =
      newWorkingLocationId !== agendaBeforeUpdate?.location &&
      newWorkingLocationId !== agendaBeforeUpdate?.office_id;

    // Improve how we store optimistic updates to ensure we're handling all location types correctly
    const optimisticUpdate = {
      location: newWorkingLocationId,
      ...(isOffice && { officeId: newWorkingLocationId })
    };
    const startOptimisticUpdate = () => {
      setOptimisticUpdates((prev) => ({
        ...prev,
        [targetDate]: optimisticUpdate
      }));

      // Set loading state
      setLoadingUpdates((prev) => ({
        ...prev,
        [targetDate]: true
      }));
    };

    // Function to clear loading state for this date
    const clearLoadingState = () => {
      setLoadingUpdates((prev) => {
        const newState = { ...prev };
        delete newState[targetDate];
        return newState;
      });
    };

    // Function to remove optimistic update in case of error
    const clearOptimisticUpdate = () => {
      setOptimisticUpdates((prev) => {
        const newState = { ...prev };
        delete newState[targetDate];
        return newState;
      });
    };

    try {
      const performUpdate = async () => {
        try {
          let hasPolicies = false;

          if (
            newWorkingLocationId === WL_PRESENCE_OOO &&
            isWorkingLocationsWithOOOFeaturesEnabled(authenticatedUser)
          ) {
            const res = await api.client.leave.allApplicablePolicies({
              tz: deviceTz,
              start: firstMondayBeforeStr,
              end: endDateForWeekStr
            });
            const allPolicies = res.filter((x) => !!x && !!x.policy);
            hasPolicies =
              (allPolicies ?? []).filter(
                (x) => x.policy.policy_type !== WL_PRESENCE_REMOTE
              ).length > 0;
          }

          if (
            newWorkingLocationId === WL_PRESENCE_OOO &&
            hasPolicies &&
            isWorkingLocationsWithOOOFeaturesEnabled(authenticatedUser)
          ) {
            if (hasPolicies) {
              setOooRequest({ date: targetDate, type: "ooo" });
              clearOptimisticUpdate();
            }
          } else if (
            newWorkingLocationId === WL_PRESENCE_REMOTE &&
            remotePolicy &&
            remotePolicy.status === "pending"
          ) {
            setOooRequest({ date: targetDate, type: "remote" });
            clearOptimisticUpdate();
          } else if (
            newWorkingLocationId === WL_PRESENCE_REMOTE &&
            remotePolicy &&
            remotePolicy.status === "approved"
          ) {
            // we should silently send the Remote Request because is not approvable
            await api.client.leave.createRequest({
              policy_type: remotePolicy.type,
              reason: "",
              starts: `${targetDate}T00:00:00.000Z`,
              ends: `${targetDate}T23:59:59.000Z`,
              time_zone_id: deviceTz
            });
          } else {
            await api.client.schedules.updateWorkingLocationDay(
              location,
              dayNumber,
              targetDate
            );
          }
          await invalidateQueries();

          if (
            isOffice &&
            orgRequiresReservationAfterPickingAnOffice(authenticatedUser) &&
            isChangingOffice
          ) {
            const targetOffice = (
              authenticatedUser.offices?.offices ?? []
            ).find((x) => x.id === newWorkingLocationId);
            openReserveModal(targetDate, targetOffice);
          }
        } catch (e) {
          clearOptimisticUpdate();
          toast.error("Error updating working location");
          // biome-ignore lint/suspicious/noConsoleLog: <explanation>
          console.log("Error updating working location", e);
          throw e; // Re-throw to be caught by the outer try-catch
        }
      };

      if (reservations.length && isChangingOffice) {
        // TBD: Remove this section once api will support removing virtual reservations -->
        const virtualReservation = reservations.find(
          (x: ISchedule) => x.is_virtual
        );
        if (virtualReservation) {
          const start = format(
            getTimeDateObjectFromString(virtualReservation.start),
            "HH:mm"
          );
          toast.error(
            `You can\'t change your location for this day because of the reservation starting at ${start}.`
          );
          queryClient.invalidateQueries({
            queryKey: ["myAgenda"],
            exact: false
          });
          queryClient.invalidateQueries(["tableOverview"]);
          // <-- logic in case of virtual reservations
        } else {
          setOnConfirmDialogCallback(() => async () => {
            // Start optimistic only if user confirms the delete
            startOptimisticUpdate();
            try {
              const deleteAll = reservations.map((x: ISchedule) =>
                api.client.schedules.delete({
                  schedule: x.id,
                  recurring: false
                })
              );
              await Promise.all(deleteAll);
              await performUpdate();
              setOnConfirmDialogCallback(null);
            } catch (e) {
              clearOptimisticUpdate();
              toast.error(
                "Sorry we couldn't delete your reservations. Please try again."
              );
            } finally {
              clearLoadingState();
            }
          });
        }
      } else {
        // start optimistic update when user don't need to confirm the delete
        startOptimisticUpdate();
        await performUpdate();
        await queryClient.invalidateQueries({
          queryKey: ["myAgenda"],
          exact: false
        });
      }
    } catch (error) {
      // Any uncaught errors will be handled here
      clearOptimisticUpdate();
    } finally {
      // Always ensure loading state is cleared with a slight delay
      // This helps prevent flashes during fast operations
      setTimeout(clearLoadingState, 300);
    }
  };

  const nextWeek = useCallback(() => {
    if (!scheduleMeData?.data) {
      return;
    }
    setWeekDate(nextWeekDate ?? "");

    // do this on data update
  }, [scheduleMeData?.data]);

  const prevWeek = useCallback(() => {
    if (!scheduleMeData?.data) {
      return;
    }
    setWeekDate(previousWeekDate ?? "");

    // do this on data update
  }, [scheduleMeData?.data]);

  const getLocationForDate = useCallback(
    (date: string, currentLocation: AgendaDay | null) => {
      const optimistic = optimisticUpdates[date];

      if (optimistic) {
        // We need to use type assertion to handle the complex type conversion
        const mergedLocation = {
          ...(currentLocation || {}),
          location: optimistic.location,
          ...(optimistic.officeId ? { office_id: optimistic.officeId } : {})
        } as AgendaDay;

        return mergedLocation;
      }

      return currentLocation;
    },
    [optimisticUpdates]
  );

  const isLocationLoading = useCallback(
    (date: string) => {
      const dateKey = date.split("T")[0]; // Get just the date part
      return !!loadingUpdates[dateKey];
    },
    [loadingUpdates]
  );

  return {
    authenticatedUser,
    weekDate,
    setWeekDate,
    scheduleMeData,
    officeAvailabilityData,
    availableDays: availableDays || defaultWeekDays,
    updateWorkingLocation,
    openReserveModal,
    reserveModalData,
    oooRequest,
    setOooRequest,
    onConfirmDialogCallback,
    setOnConfirmDialogCallback,
    setReserveModalData,
    nextWeek,
    prevWeek,

    invalidateQueries,

    getLocationForDate,
    isLocationLoading,
    isLoadingOfficeAvailability,
    optimisticUpdates
  };
}
